import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { GalleryMedia } from '../../types';
import InfoBlock from '../../InfoBlock';
import ParametersBlock from '../../ParametersBlock';
import ReferenceImagesBlock from '../../ReferenceImagesBlock';
import { ActionButton } from '../../../Common/Buttons/ActionButton';
import {
  CloseIcon,
  PlusIcon,
  DownloadIcon,
  AnimateIcon,
  UpscaleIcon,
  EditIcon,
  RegenerateIcon,
  InfoIcon,
} from '../../icons';
import styles from './DesktopSidebar.module.css';

interface DesktopSidebarProps {
  image: GalleryMedia;
  isVideo: boolean;
  isSvg: boolean;
  isSidebarOpen: boolean;
  canRegenerate: boolean;
  isDownloading: boolean;
  confirmingDelete: boolean;
  onClose: () => void;
  onRegenerate: (e: React.MouseEvent) => void;
  onUse: (e: React.MouseEvent) => void;
  onUpscale: (e: React.MouseEvent) => void;
  onEditImage: (e: React.MouseEvent) => void;
  onAnimate: (e: React.MouseEvent) => void;
  onUseVideo: (e: React.MouseEvent) => void;
  onDownload: (e?: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  onContentClick: (e: React.MouseEvent) => void;
  hasOnUse: boolean;
  hasOnUpscale: boolean;
  hasOnEditImage: boolean;
  hasOnAnimate: boolean;
  hasOnUseVideo: boolean;
  onUseReference?: (url: string, isVideo: boolean) => void;
  hideDownload?: boolean;
  hideDelete?: boolean;
  hideParameters?: boolean;
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
}

export const DesktopSidebar: React.FC<DesktopSidebarProps> = ({
  image,
  isVideo,
  isSvg,
  isSidebarOpen,
  canRegenerate,
  isDownloading,
  confirmingDelete,
  onClose,
  onRegenerate,
  onUse,
  onUpscale,
  onEditImage,
  onAnimate,
  onUseVideo,
  onDownload,
  onDelete,
  onContentClick,
  hasOnUse,
  hasOnUpscale,
  hasOnEditImage,
  hasOnAnimate,
  hasOnUseVideo,
  onUseReference,
  hideDownload,
  hideDelete,
  hideParameters,
  primaryAction,
}) => {
  const { t } = useTranslation(['image', 'common']);

  return (
    <div className={clsx(styles.sidebar, !isSidebarOpen && styles.collapsed)} onClick={onContentClick}>
      <div className={styles.sidebarInner}>
        <div className={styles.sidebarHeader}>
          <h2 className={styles.sidebarTitle}>{t('gallery.details')}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className={clsx(styles.sidebarContent, 'galleryScrollbar')}>
          {image.shots && image.shots.length > 0 ? (
            image.shots.map((shot, i) => (
              <InfoBlock
                key={shot.id}
                label={`${t('gallery.scene')} ${i + 1} (${shot.duration}${t('gallery.sec')})`}
                value={shot.prompt}
                canCopy
                collapsible
              />
            ))
          ) : !image.isUpscaled && (
            <InfoBlock
              label={image.presetName ? t('gallery.presetLabel') : t('gallery.prompt')}
              value={image.metadata.prompt}
              canCopy={!image.presetName}
              collapsible
            />
          )}
          {image.referenceImages && image.referenceImages.length > 0 && (
            <ReferenceImagesBlock
              images={image.referenceImages}
              labels={image.referenceImageLabels}
              isVideoFlags={image.referenceIsVideo}
              onUseReference={onUseReference}
            />
          )}
          {!hideParameters && (
            <ParametersBlock
              model={image.metadata.model}
              provider={image.provider}
              time={image.metadata.generationTime}
              format={image.metadata.format}
              quality={image.metadata.quality}
              resolution={image.metadata.resolution}
              duration={image.metadata.duration ? `${image.metadata.duration}с` : undefined}
              stylize={image.metadata.stylize}
              chaos={image.metadata.chaos}
              weird={image.metadata.weird}
              seed={image.metadata.seed}
              niji={image.metadata.niji}
              mjQuality={image.metadata.mjQuality}
            />
          )}
        </div>

        {/* Actions — pinned between the scrollable content and the Download/Delete footer.
            Stays in place while Prompt/References/Parameters scroll independently above. */}
        {(canRegenerate || hasOnUse || hasOnUpscale || hasOnEditImage || hasOnAnimate || hasOnUseVideo) && (
          <div className={styles.actionsPinned}>
            <div className={styles.actionsBlock}>
              <div className={styles.actionsHeader}>
                <span className={styles.actionsHeaderIcon}><InfoIcon /></span>
                <span className={styles.actionsHeaderLabel}>{t('gallery.actions')}</span>
              </div>
              <div className={clsx(styles.actionButtonsGrid, (!canRegenerate || (isVideo ? !hasOnUseVideo : !hasOnUse)) && styles.singleButton)}>
                {canRegenerate && (
                  <div className={styles.actionButtonWrapper}>
                    <ActionButton icon={<RegenerateIcon />} label={t('gallery.repeat')} onClick={onRegenerate} />
                    <div className={styles.tooltipAbove}>
                      <span>{t('gallery.repeatHint')}</span>
                      <div className={styles.tooltipArrowDown} />
                    </div>
                  </div>
                )}
                {!isVideo && !isSvg && hasOnUse && (
                  <div className={styles.actionButtonWrapper}>
                    <ActionButton icon={<PlusIcon />} label={t('gallery.useAsReference')} onClick={onUse} />
                    <div className={styles.tooltipAbove}>
                      <span>{t('gallery.useAsReferenceHint')}</span>
                      <div className={styles.tooltipArrowDown} />
                    </div>
                  </div>
                )}
                {!isVideo && !isSvg && hasOnUpscale && (
                  <div className={styles.actionButtonWrapper}>
                    <ActionButton icon={<UpscaleIcon />} label={t('gallery.upscale')} onClick={onUpscale} />
                    <div className={styles.tooltipAbove}>
                      <span>{t('gallery.upscaleHint')}</span>
                      <div className={styles.tooltipArrowDown} />
                    </div>
                  </div>
                )}
                {!isVideo && !isSvg && hasOnEditImage && (
                  <div className={styles.actionButtonWrapper}>
                    <ActionButton icon={<EditIcon />} label={t('gallery.editImage')} onClick={onEditImage} />
                    <div className={styles.tooltipAbove}>
                      <span>{t('gallery.editImageHint')}</span>
                      <div className={styles.tooltipArrowDown} />
                    </div>
                  </div>
                )}
                {!isVideo && !isSvg && hasOnAnimate && (
                  <div className={styles.actionButtonWrapper}>
                    <ActionButton icon={<AnimateIcon />} label={t('gallery.animate')} onClick={onAnimate} />
                    <div className={styles.tooltipAbove}>
                      <span>{t('gallery.animateHint')}</span>
                      <div className={styles.tooltipArrowDown} />
                    </div>
                  </div>
                )}
                {isVideo && hasOnUseVideo && (
                  <div className={styles.actionButtonWrapper}>
                    <ActionButton icon={<EditIcon />} label={t('gallery.useInVideoEdit')} onClick={onUseVideo} />
                    <div className={styles.tooltipAbove}>
                      <span>{t('gallery.useInVideoEditHint')}</span>
                      <div className={styles.tooltipArrowDown} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {(primaryAction || !hideDownload || !hideDelete) && (
          <div className={styles.sidebarFooter}>
            <div className={styles.footerActions}>
              {primaryAction ? (
                <ActionButton
                  icon={primaryAction.icon}
                  label={primaryAction.label}
                  onClick={primaryAction.onClick}
                  active
                  noGlow
                />
              ) : !hideDownload && (
                <ActionButton
                  icon={isDownloading ? <i className="fa-solid fa-circle-notch fa-spin" /> : <DownloadIcon />}
                  label={isDownloading ? t('btn.downloading', { ns: 'common' }) : t('btn.download', { ns: 'common' })}
                  onClick={onDownload}
                  disabled={isDownloading}
                  active
                  noGlow
                />
              )}
              {!hideDelete && (
                <button className={clsx(styles.deleteButton, confirmingDelete && styles.confirming)} onClick={onDelete}>
                  <CloseIcon />
                  {confirmingDelete && <span>{t('gallery.confirmDeleteInline')}</span>}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
